import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';
import type {
  PsychologySession,
  PsychologyPricing,
  PsychologyWizardStep,
  GenerationStatus,
  MethodologySearchResult,
} from '../types/psychology';

interface PsychologyState {
  pricing: PsychologyPricing | null;
  serviceAvailable: boolean;

  session: PsychologySession | null;
  sessions: PsychologySession[];

  outline: string | null;
  theses: string | null;
  outlineGenerationCount: number;

  generationStatus: GenerationStatus | null;
  generationProgress: string | null;

  selectedMethods: string[];
  methodSearchResults: MethodologySearchResult[];
  methodsNotFound: string[];
  calculatedPrice: number | null;

  wizardStep: PsychologyWizardStep;
  isLoading: boolean;
  loadingMessage: string;
  error: string | null;

  /** Смонтирован ли сейчас экран психологии — чтобы не дублировать тост о готовности,
   *  когда пользователь и так смотрит на завершение вживую (см. socketSlice.ts). */
  isPageActive: boolean;
}

const initialState: PsychologyState = {
  pricing: null,
  serviceAvailable: true,

  session: null,
  sessions: [],

  outline: null,
  theses: null,
  outlineGenerationCount: 0,

  generationStatus: null,
  generationProgress: null,

  selectedMethods: [],
  methodSearchResults: [],
  methodsNotFound: [],
  calculatedPrice: null,

  wizardStep: 'form',
  isLoading: false,
  loadingMessage: '',
  error: null,

  isPageActive: false,
};

const psychologySlice = createSlice({
  name: 'psychology',
  initialState,
  reducers: {
    setPsychPricing(state, action: PayloadAction<PsychologyPricing>) {
      state.pricing = action.payload;
    },
    setPsychServiceAvailable(state, action: PayloadAction<boolean>) {
      state.serviceAvailable = action.payload;
    },
    setPsychSession(state, action: PayloadAction<PsychologySession | null>) {
      state.session = action.payload;
      if (action.payload?.outline) {
        state.outline = action.payload.outline;
      }
      if (action.payload?.theses) {
        state.theses = action.payload.theses;
      }
    },
    setPsychSessions(state, action: PayloadAction<PsychologySession[]>) {
      state.sessions = action.payload;
    },
    setPsychOutline(state, action: PayloadAction<{ outline: string; theses: string }>) {
      state.outline = action.payload.outline;
      state.theses = action.payload.theses;
      state.outlineGenerationCount += 1;
    },
    setPsychOutlineEdited(state, action: PayloadAction<{ outline: string; theses: string }>) {
      state.outline = action.payload.outline;
      state.theses = action.payload.theses;
    },
    setPsychGenerationStatus(state, action: PayloadAction<GenerationStatus | null>) {
      state.generationStatus = action.payload;
      if (action.payload?.progress) {
        state.generationProgress = action.payload.progress;
      }
    },
    setPsychGenerationProgress(state, action: PayloadAction<string>) {
      state.generationProgress = action.payload;
    },
    setPsychSelectedMethods(state, action: PayloadAction<string[]>) {
      state.selectedMethods = action.payload;
    },
    addPsychMethod(state, action: PayloadAction<string>) {
      if (!state.selectedMethods.includes(action.payload)) {
        state.selectedMethods.push(action.payload);
      }
    },
    removePsychMethod(state, action: PayloadAction<string>) {
      state.selectedMethods = state.selectedMethods.filter((m) => m !== action.payload);
    },
    setPsychMethodSearchResults(state, action: PayloadAction<MethodologySearchResult[]>) {
      state.methodSearchResults = action.payload;
    },
    setPsychMethodsNotFound(state, action: PayloadAction<string[]>) {
      state.methodsNotFound = action.payload;
    },
    setPsychCalculatedPrice(state, action: PayloadAction<number | null>) {
      state.calculatedPrice = action.payload;
    },
    setPsychWizardStep(state, action: PayloadAction<PsychologyWizardStep>) {
      state.wizardStep = action.payload;
    },
    setPsychLoading(state, action: PayloadAction<{ loading: boolean; message?: string }>) {
      state.isLoading = action.payload.loading;
      state.loadingMessage = action.payload.message || '';
      if (action.payload.loading) {
        state.error = null;
      }
    },
    setPsychError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },
    setPsychPageActive(state, action: PayloadAction<boolean>) {
      state.isPageActive = action.payload;
    },
    resetPsychology(state) {
      Object.assign(state, {
        ...initialState,
        pricing: state.pricing,
        serviceAvailable: state.serviceAvailable,
        sessions: state.sessions,
        isPageActive: state.isPageActive,
      });
    },
  },
});

export const {
  setPsychPricing,
  setPsychServiceAvailable,
  setPsychSession,
  setPsychSessions,
  setPsychOutline,
  setPsychOutlineEdited,
  setPsychGenerationStatus,
  setPsychGenerationProgress,
  setPsychSelectedMethods,
  addPsychMethod,
  removePsychMethod,
  setPsychMethodSearchResults,
  setPsychMethodsNotFound,
  setPsychCalculatedPrice,
  setPsychWizardStep,
  setPsychLoading,
  setPsychError,
  setPsychPageActive,
  resetPsychology,
} = psychologySlice.actions;

export const selectPsychology = (state: RootState) => state.psychology;

export default psychologySlice.reducer;
